import { ChevronRightIcon, SparklesIcon } from "lucide-react";
import { useState } from "react";
import { Link, NavLink } from "react-router";
import { useT } from "../shell/i18n";
import { IconAction, ON_SHELL_CONTROL } from "../ui/button";
import { useModuleNav } from "../ui/module-nav";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import {
  ALONGSIDE,
  type Destination,
  entryPath,
  moduleFor,
  type RailGroup,
  type Section,
} from "./destinations";
import { isNavigationFolder, type NavigationFolder, navigationEntries } from "./navigation-folders";
import { useUnreadNotifications } from "./notification-updates";

// `NavGroup` WAS DECLARED HERE and is `RailGroup` from `destinations.ts` now. It was a
// structural twin of what `groupsFor` returns — `{ label, items }` — which is exactly the
// arrangement that cannot carry a new member: a group that knows whether it COLLAPSES has
// to say so to both renderers, and a local interface listing two of the three fields would
// have gone on type-checking while silently dropping the third. The declaration owns the
// shape it produces; this file renders it.

/**
 * The navigation, rendered identically in the rail and in the sheet.
 *
 * ONE `nav` with headed groups rather than four navs: a screen reader announces one
 * navigation landmark and the headings inside it, which is the same structure a
 * sighted reader sees.
 */
/**
 * THE RAIL AS ICONS, which is what the shell holds at every width above `lg`.
 *
 * The wide, labelled `Navigation` below is not deleted: it is what the sheet renders on
 * a narrow screen, where there is room for words and no room for a permanent column.
 * Both read the same `groups`, so a destination cannot exist in one and not the other —
 * the defect that made the rail and the command palette two lists in the first place.
 *
 * # An icon alone is not a name
 *
 * Every item carries a TOOLTIP and an `aria-label`, from the same string. A rail of
 * unlabelled glyphs is a rail somebody learns by clicking each one, and the group
 * headings that carried the meaning in the wide version have nowhere to go here — so a
 * separator stands in for the grouping and the name moves onto the item.
 */
/**
 * WHAT SITS ON THE RIGHT: the surfaces that accompany the work.
 *
 * Two, and deliberately not more. The utility rail is where a product puts the things
 * you glance at while doing something else — not a second navigation, and not a home for
 * whatever did not fit on the left. A third entry here should have to argue for itself.
 */
export const UTILITY = ALONGSIDE;

// `ON_SHELL_CONTROL` WAS DECLARED HERE and is imported from `ui/button.tsx` now, with its
// measurement and its argument. The move is the fix rather than tidying: it said it was
// named once "so a fourth control cannot inherit a copy with a piece missing", and the
// fourth control had already inherited one — `ui/account.tsx`'s avatar trigger had the two
// background halves and none of the three colour halves. It could not have done otherwise,
// because `ui/` may not import from `app/`, so the one consumer outside this file was
// unable to reach the constant at all. A shared value declared inside one of its consumers
// is reachable by that consumer alone.

/**
 * THE SHAPE OF A RAIL ENTRY, whether it names a destination or an area of them.
 *
 * Written once because the two sit in the same column and a reader has to see them as one
 * list. They are NOT the same element — a destination is an `<a>` and an area is a
 * `<button>`, see `GroupEntry` — and this is exactly why the skin is shared rather than
 * inherited from either: two entries that look like one list and are two elements is the case
 * where a class written twice drifts, and the reader is owed one column whatever the semantics
 * underneath say.
 */
const RAIL_ENTRY =
  "flex size-9 items-center justify-center rounded-lg no-underline transition-colors " +
  "focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2";
const RAIL_ENTRY_HERE = "bg-surface-selected text-foreground";
const RAIL_ENTRY_ELSEWHERE = "text-muted-foreground hover:bg-surface-hover hover:text-foreground";

function useUtilityName(item: Destination) {
  const t = useT();
  const count = useUnreadNotifications();
  const label = t(item.label);
  return item.to === "/inbox" && count ? `${label}, ${t("notify.unreadCount", { count })}` : label;
}

function UtilityGlyph({ item }: { readonly item: Destination }) {
  const count = useUnreadNotifications();
  return (
    <span className="relative grid size-6 place-items-center" aria-hidden="true">
      <item.icon className="size-4 shrink-0" />
      {item.to === "/inbox" && count ? (
        <span className="absolute top-0 right-0 size-2.5 rounded-full border-2 border-surface-panel bg-surface-action" />
      ) : null}
    </span>
  );
}

/**
 * A group is a drill parent, not a page. Pressing it replaces the sidebar's contents and
 * leaves the current route intact; only a declared child can navigate. Its selected styling
 * still follows the address, so inspecting a different group never claims the reader moved.
 */
function GroupEntry({ group, at }: { readonly group: RailGroup; readonly at: string }) {
  const t = useT();
  const label = t(group.label);
  const { showArea } = useModuleNav();
  // LIT BY THE ADDRESS BEING ONE THIS GROUP OFFERS, rather than by the address being in this
  // group. The two differ for a caller who has typed the address of something they cannot
  // reach: `groupsFor` has already dropped it from `items`, so the wider test would light an
  // entry whose sidebar does not contain the place you are.
  const module = moduleFor(at);
  const inside = group.items.some((item) => item.to === module?.to);
  const glyph = group.icon ? <group.icon className="size-4 shrink-0" aria-hidden="true" /> : null;
  const skin = `${RAIL_ENTRY} ${inside ? RAIL_ENTRY_HERE : RAIL_ENTRY_ELSEWHERE}`;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button type="button" aria-label={label} onClick={() => showArea(group)} className={skin}>
            {glyph}
          </button>
        }
      />
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

export function IconRail({
  groups,
  at,
}: {
  readonly groups: readonly RailGroup[];
  readonly at: string;
}) {
  const t = useT();
  // EVERY ENTRY SAYS WHAT THE SIDEBAR IS NOW, and the module arm has to say it EXPLICITLY.
  //
  // An opened area expires when the address moves, which covers every ordinary press — but
  // pressing the rail entry for the module you are already standing on moves nothing, so
  // without this the reader who opened Administration from `/leave` could press Leave and
  // watch the eight stay put. One line, in the one component that draws both arms.
  const { showArea } = useModuleNav();
  // WHICH MODULE THE ADDRESS IS IN, resolved ONCE and by the shell's own rule — the same
  // `moduleFor` the breadcrumb, the module sidebar and `GroupEntry` six lines up all read.
  // See the module arm below for why the router may not be asked this.
  const module = moduleFor(at);
  return (
    <nav aria-label={t("nav.label")} className="flex min-h-0 flex-1 flex-col items-center gap-1">
      {groups.map((group, index) => (
        <div
          key={group.label}
          className={`flex w-full flex-col items-center gap-1 ${
            group.label === "nav.group.administration" ? "mt-auto" : ""
          }`}
        >
          {/* A RULE RATHER THAN A HEADING. The wide rail names each group; here there is
              no width for a word, and a visually-hidden heading between two icons is a
              level in the outline that a sighted reader has no counterpart for. The
              `<nav>`'s own label already names the landmark.

              IT SURVIVES THE COLLAPSE, and is not now redundant: the rule divides the
              AREAS, and a collapsed group is one entry among the areas rather than the
              only thing a rule could have been separating. */}
          {index > 0 ? <hr className="my-1 w-6 border-0 border-t border-line-subtle" /> : null}
          {/* THE `<li>`s STAY DIRECT CHILDREN OF THE `<ul>` in both arms, which is what
              keeps "list, N items" honest — a wrapper around the items would take the
              count with it, and a collapsed group is one item in this list rather than
              eight hidden ones. */}
          <ul className="m-0 flex list-none flex-col items-center gap-1 p-0">
            {group.collapsed ? (
              <li>
                <GroupEntry group={group} at={at} />
              </li>
            ) : (
              group.items.map((item) => (
                <li key={item.to}>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        // `Link` AND NOT `NavLink`, which is the rule `GroupEntry` above and
                        // `ModuleNavLink` one region to the right were each moved onto after
                        // being measured, and this was the last site in the shell still
                        // letting the router form its own opinion.
                        //
                        // A `NavLink` matches by PREFIX (`end` only buys an exact match, which
                        // would unlight Leave on `/leave/requests`), and the shell resolves the
                        // same question by LONGEST prefix. The two agree for every address in
                        // the product except one, and the exception is a destination declared
                        // UNDERNEATH another destination's path — which `moduleFor`'s own
                        // docstring says is a supported shape and which the rail could not
                        // express. Measured in the browser at `/leave/setup`: the rail lit
                        // "Leave" AND "Administration", and the one announcing
                        // `aria-current="page"` was Leave — so a reader who pressed "Calendar
                        // setup" under Administration was told by the rail that they were in
                        // the leave module. Reported as exactly that.
                        <Link
                          to={entryPath(item)}
                          viewTransition={at !== entryPath(item)}
                          aria-label={t(item.label)}
                          // THE FILL IS THE MODULE and the announcement is the PAGE, which are
                          // two different claims and were one while this was a `NavLink`. The
                          // fill says *you are working in here*, which is true across a
                          // module's sections; `aria-current` says *this link is the page you
                          // are on*, which on `/leave/requests` is false of a link to
                          // `/leave` — and the sidebar's own row is what says it truthfully.
                          aria-current={at === entryPath(item) ? "page" : undefined}
                          onClick={() => showArea(null)}
                          className={`${RAIL_ENTRY} ${
                            module?.to === item.to ? RAIL_ENTRY_HERE : RAIL_ENTRY_ELSEWHERE
                          }`}
                        >
                          <item.icon className="size-4 shrink-0" aria-hidden="true" />
                        </Link>
                      }
                    />
                    <TooltipContent side="right">{t(item.label)}</TooltipContent>
                  </Tooltip>
                </li>
              ))
            )}
          </ul>
        </div>
      ))}
    </nav>
  );
}

export function UtilityEntry({ item, at }: { readonly item: Destination; readonly at: string }) {
  const label = useUtilityName(item);
  const skin = `${RAIL_ENTRY} ${
    moduleFor(at)?.to === item.to ? RAIL_ENTRY_HERE : RAIL_ENTRY_ELSEWHERE
  }`;

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <NavLink
            to={entryPath(item)}
            viewTransition={at !== entryPath(item)}
            aria-label={label}
            className={skin}
          >
            <UtilityGlyph item={item} />
          </NavLink>
        }
      />
      <TooltipContent side="left">{label}</TooltipContent>
    </Tooltip>
  );
}

/** A utility action opens a pane; unlike the other rail entries it has no URL. */
export function ChatUtilityAction({
  onClick,
  compact = false,
}: {
  readonly onClick: () => void;
  readonly compact?: boolean;
}) {
  const t = useT();
  return (
    <IconAction
      label={t("ai.chat.title")}
      onClick={onClick}
      className={compact ? `size-8 ${ON_SHELL_CONTROL}` : `${RAIL_ENTRY} ${RAIL_ENTRY_ELSEWHERE}`}
    >
      <SparklesIcon className="size-4" aria-hidden="true" />
    </IconAction>
  );
}

/** The utility rail's compact equivalent while the permanent right rail has no room. */
export function CompactUtilityEntry({
  item,
  at,
}: {
  readonly item: Destination;
  readonly at: string;
}) {
  const label = useUtilityName(item);
  const target = entryPath(item);

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Link
            to={target}
            viewTransition={at !== target}
            aria-label={label}
            className={`grid size-8 place-items-center rounded-md ${ON_SHELL_CONTROL}`}
          >
            <UtilityGlyph item={item} />
          </Link>
        }
      />
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * THE SAME GROUP IN THE SHEET, where there is room for words and no room for a menu.
 *
 * A menu opening out of a sheet on a phone is a second overlay over an overlay, so the
 * collapsed group is a DISCLOSURE here: the heading gains a button and the list it already
 * had is what hides. The two renderers therefore draw a collapsed group differently and
 * neither decides WHETHER it is collapsed — `groupsFor` does, once, or the rail and the
 * sheet would disagree the day the rule moved.
 *
 * # Open because of the ADDRESS, and remembered nowhere
 *
 * `useState` seeded from where the reader is, in a component the sheet unmounts on close —
 * so the initial state is recomputed every time the sheet is opened and there is no
 * `localStorage` anywhere near it. A pasted `/payroll` opens the sheet with administration
 * already expanded and the payroll link in view.
 *
 * That is the whole argument for not remembering it: a remembered disclosure answers
 * *what did I last press* to a question the address already answers, and answers it wrongly
 * for exactly the case nobody tests — a link somebody was SENT. `module-nav.tsx` does
 * remember its collapse and that stays right, because *give me more room* is a preference
 * about room; *which area am I in* is not a preference at all.
 *
 * It is still a real toggle after that: seeding the state is not the same as forcing it, and
 * a reader who opens the sheet to go somewhere ELSE must be able to shut the group they are
 * standing in.
 */
function NavigationGroup({ group, at }: { readonly group: RailGroup; readonly at: string }) {
  const t = useT();
  const module = moduleFor(at);
  const [open, setOpen] = useState(() => group.items.some((item) => item.to === module?.to));
  const id = `nav-group-${group.label}`;

  return (
    <div className="flex flex-col gap-0.5">
      {/* THE HEADING KEEPS ITS LEVEL and gains a control inside it, rather than being
          replaced by one: the outline a screen reader walks is the same either way, and a
          `<button>` standing where an `<h2>` stood removes the group from that outline
          entirely. */}
      <h2 className="m-0">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={id}
          onClick={() => setOpen((was) => !was)}
          className="flex w-full items-center gap-1 rounded-lg px-2 py-1 text-left text-shell-caption font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <span className="min-w-0 flex-1 truncate">{t(group.label)}</span>
          {/* THE GLYPH SAYS WHICH WAY, and it is `aria-hidden` because `aria-expanded` has
              already said it to anybody not looking at it.

              AFTER THE LABEL, WHICH IS AN ALIGNMENT DECISION AND NOT A STYLE ONE. Leading,
              it pushed this heading's word ~20px right of the three beside it — measured in
              the running product at 420px, where the four headings are a column somebody
              scans and one of them started somewhere else. Nothing in jsdom could see that:
              it computes no layout, so a screenshot was the only instrument. */}
          <ChevronRightIcon
            className={`size-3.5 shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
            aria-hidden="true"
          />
        </button>
      </h2>
      {/* `hidden` AND NOT UNMOUNTED, and never `aria-hidden`: the list is a real list whose
          items are really unreachable while it is shut, which is what `hidden` means and
          what `aria-hidden` alone would lie about — it would leave eight tab stops behind a
          collapsed heading. */}
      <ul id={id} hidden={!open} className="m-0 flex list-none flex-col gap-0.5 p-0">
        {navigationEntries(group.items).map((entry) =>
          isNavigationFolder(entry) ? (
            <NavigationFolderItem key={entry.key} folder={entry} at={at} />
          ) : (
            <NavigationItem key={entry.to} item={entry} at={at} showIcon={false} />
          ),
        )}
      </ul>
    </div>
  );
}

/** A task family inside the compact Administration disclosure. */
function NavigationFolderItem({
  folder,
  at,
}: {
  readonly folder: NavigationFolder<Destination>;
  readonly at: string;
}) {
  const t = useT();
  const module = moduleFor(at);
  const [open, setOpen] = useState(() => folder.items.some((item) => item.to === module?.to));
  const id = `nav-folder-${folder.key}`;

  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((was) => !was)}
        className="flex h-8 w-full items-center gap-2 rounded-lg px-2 text-left text-shell-body text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        <span className="min-w-0 flex-1 truncate">{t(folder.label)}</span>
        <ChevronRightIcon
          className={`size-3.5 shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
          aria-hidden="true"
        />
      </button>
      <ul id={id} hidden={!open} className="m-0 flex list-none flex-col gap-0.5 pl-4">
        {folder.items.map((item) => (
          <NavigationFolderModuleItem key={item.to} item={item} at={at} />
        ))}
      </ul>
    </li>
  );
}

/** A module inside a task family: its press reveals screens and never chooses one. */
function NavigationFolderModuleItem({
  item,
  at,
}: {
  readonly item: Destination;
  readonly at: string;
}) {
  const t = useT();
  const active = moduleFor(at)?.to === item.to;
  const [open, setOpen] = useState(active);
  const id = `nav-folder-module-${item.to.replaceAll("/", "-")}`;

  if ((item.sections?.length ?? 0) === 0) {
    return <NavigationItem item={item} at={at} showIcon={false} />;
  }

  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((was) => !was)}
        className={[
          "flex h-8 w-full items-center gap-2 rounded-lg px-2 text-left text-shell-body transition-colors",
          active
            ? "bg-accent font-medium text-foreground"
            : "text-muted-foreground hover:bg-accent hover:text-foreground",
        ].join(" ")}
      >
        <span className="min-w-0 flex-1 truncate">{t(item.label)}</span>
        <ChevronRightIcon
          className={`size-3.5 shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
          aria-hidden="true"
        />
      </button>
      <ul
        id={id}
        hidden={!open}
        className="m-0 ms-5 flex list-none flex-col gap-0.5 border-line-subtle border-s p-0 ps-2"
      >
        {item.sections?.map((section) => (
          <NavigationSection key={section.to} section={section} at={at} />
        ))}
      </ul>
    </li>
  );
}

export function Navigation({
  groups,
  utilities,
  at,
}: {
  readonly groups: readonly RailGroup[];
  readonly utilities: readonly Destination[];
  readonly at: string;
}) {
  const t = useT();

  return (
    <nav aria-label={t("nav.label")} className="flex flex-col gap-5">
      <div className="flex flex-col gap-0.5">
        <h2 className="m-0 px-2 pb-1 text-shell-caption font-medium text-muted-foreground">
          {t("nav.utility")}
        </h2>
        <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
          {utilities.map((item) => (
            <NavigationItem key={item.to} item={item} at={at} />
          ))}
        </ul>
      </div>
      {groups.map((group) =>
        group.collapsed ? (
          <NavigationGroup key={group.label} group={group} at={at} />
        ) : (
          <div className="flex flex-col gap-0.5" key={group.label}>
            {/* SENTENCE case, not upper. An uppercase label is a heavier mark than the
              links under it, so the group headings competed with the destinations —
              the reference interfaces all set these quiet and small, and the hierarchy
              reads correctly for it. */}
            <h2 className="m-0 px-2 pb-1 text-shell-caption font-medium text-muted-foreground">
              {t(group.label)}
            </h2>
            <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
              {group.items.map((item) => (
                <NavigationItem key={item.to} item={item} at={at} />
              ))}
            </ul>
          </div>
        ),
      )}
    </nav>
  );
}

/** ONE LABELLED DESTINATION in the sheet, drawn the same inside a group and outside one. */
function NavigationItem({
  item,
  at,
  showIcon = true,
}: {
  readonly item: Destination;
  readonly at: string;
  readonly showIcon?: boolean;
}) {
  const t = useT();
  return (
    <li>
      {/* `Link` AND NOT `NavLink`, and `moduleFor` rather than the router's own matching —
          the icon rail's module arm carries the measurement and this is the same list drawn
          for a narrower window. A `NavLink` matches by PREFIX, so it lit BOTH "Leave" and
          the group holding `/leave/setup` while the reader stood on the latter; `end` is not
          the fix, because an exact match would unlight Leave on `/leave/requests`.

          The current page differs by its LABEL and a quiet fill, and by
          nothing else — no accent bar, no coloured text. An earlier version
          reached into the tag palette for a blue fill, which made a
          navigation item look like a tag. */}
      <Link
        to={entryPath(item)}
        aria-current={at === entryPath(item) ? "page" : undefined}
        className={[
          "flex h-8 items-center gap-2 rounded-lg px-2 text-shell-body no-underline transition-colors",
          moduleFor(at)?.to === item.to
            ? "bg-accent font-medium text-foreground"
            : "text-muted-foreground hover:bg-accent hover:text-foreground",
        ].join(" ")}
      >
        {showIcon ? <item.icon className="size-4 shrink-0" /> : null}
        <span className="truncate">{t(item.label)}</span>
      </Link>
      {moduleFor(at)?.to === item.to && (item.sections?.length ?? 0) > 0 ? (
        <ul className="m-0 ms-5 mt-0.5 flex list-none flex-col gap-0.5 border-line-subtle border-s p-0 ps-2">
          {item.sections?.map((section) => (
            <NavigationSection key={section.to} section={section} at={at} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function NavigationSection({ section, at }: { readonly section: Section; readonly at: string }) {
  const t = useT();
  const pathOwnsAddress =
    at === section.to ||
    (section.ownsChildren && at.startsWith(`${section.to}/`)) ||
    section.children?.some(
      (child) => at === child.to || (child.ownsChildren && at.startsWith(`${child.to}/`)),
    );
  const [expanded, setExpanded] = useState(pathOwnsAddress ?? false);
  const skin = [
    "flex min-h-8 w-full items-center gap-2 rounded-lg px-2 text-left text-shell-body no-underline transition-colors",
    pathOwnsAddress
      ? "bg-accent font-medium text-foreground"
      : "text-muted-foreground hover:bg-accent hover:text-foreground",
  ].join(" ");

  return (
    <li>
      {(section.children?.length ?? 0) > 0 ? (
        <>
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((current) => !current)}
            className={skin}
          >
            <span className="min-w-0 flex-1 truncate">{t(section.label)}</span>
            <ChevronRightIcon
              className={`size-4 shrink-0 transition-transform ${expanded ? "rotate-90" : ""}`}
              aria-hidden="true"
            />
          </button>
          {expanded ? (
            <ul className="m-0 ms-3 flex list-none flex-col gap-0.5 p-0 ps-2">
              {section.children?.map((child) => (
                <NavigationSection key={child.to} section={child} at={at} />
              ))}
            </ul>
          ) : null}
        </>
      ) : (
        <Link
          to={section.to}
          aria-current={at === section.to ? "page" : undefined}
          className={skin}
        >
          <span className="min-w-0 flex-1 truncate">{t(section.label)}</span>
        </Link>
      )}
    </li>
  );
}
